import { useEffect, createContext, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';

// Create context with a ref object instead of a number
const ModalContext = createContext({ current: 0 });

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const modalCount = useRef(0);

  return <ModalContext.Provider value={modalCount}>{children}</ModalContext.Provider>;
};

export const Modal = ({
  isOpen,
  onClose,
  children,
  className = '',
  style = {}
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  style?: string;
}) => {
  const modalCount = useContext(ModalContext);
  const modalIndex = useRef(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      modalCount.current += 1;
      modalIndex.current = modalCount.current;
    }
    return () => {
      if (isOpen) {
        document.body.style.overflow = 'unset';
        modalCount.current -= 1;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const baseZIndex = 40;
  const currentZIndex = baseZIndex + modalIndex.current * 10;

  const content = (
    <div
      className="fixed top-0 left-0 right-0 bottom-0 flex items-center justify-center"
      style={{ zIndex: currentZIndex, ...style }}
    >
      <button
        type="button"
        className="bg-slate-800/70 fixed top-0 left-0 right-0 bottom-0 absolute"
        style={{ zIndex: currentZIndex }}
        onClick={onClose}
      />
      <div style={{ zIndex: currentZIndex + 1 }} className={className}>
        {children}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
